'use client';

import clsx from 'clsx/lite';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { FreeMode } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { getMaskedPathSync } from '~/utils/url';

interface InstagramItem {
  link: string;
  img_src: string;
  alt_text: string;
}

export function Instagram({
  data,
  team = '',
}: {
  data: InstagramItem[];
  team?: string;
}) {
  const [maskedUrls, setMaskedUrls] = useState<{ [key: string]: string }>({});
  const marqueeItems = Array.from({ length: 8 });

  const igAccount =
    {
      bandits: 'ausl_bandits',
      blaze: 'ausl_blaze',
      talons: 'ausl_talons',
      volts: 'ausl_volts',
    }[team] || 'theauslofficial';

  useEffect(() => {
    // Process all image URLs
    const processMaskedUrls = () => {
      const urlMap: { [key: string]: string } = {};
      for (const item of data) {
        urlMap[item.img_src] = getMaskedPathSync(item.img_src, team);
      }
      setMaskedUrls(urlMap);
    };

    processMaskedUrls();
  }, [data, team]);

  return (
    <div className="Socials inline-flex items-center justify-start self-stretch border-b border-white bg-black">
      <div className="block-19 w-full !py-[25px] lg:!py-[48px]">
        <a
          href={`https://www.instagram.com/${igAccount}/`}
          className={clsx(
            'whitespace-nowrap text-[28px] font-black uppercase text-white hover:text-[--theme-primary]',
            'marquee',
          )}
          aria-label="Follow on Instagram"
          target="_blank"
        >
          <div className={clsx('marquee__content')}>
            {marqueeItems?.map((marqueeItem, index) => (
              <span
                className={clsx('flex items-center gap-x-4', 'marquee__item')}
                key={index}
              >
                <InstaIcon />
                Follow us at @{igAccount}
              </span>
            ))}
          </div>
          <div className={clsx('marquee__content')} aria-hidden="true">
            {marqueeItems?.map((marqueeItem, index) => (
              <span className="flex items-center gap-x-4" key={index}>
                <InstaIcon />
                Follow us at @{igAccount}
              </span>
            ))}
          </div>
        </a>

        <div className="mx-auto mt-10 w-full max-w-[1299px] md:mt-[32px]">
          <Swiper
            spaceBetween={32}
            slidesPerView={'auto'}
            freeMode={true}
            modules={[FreeMode]}
            className="slider-ig js-flickity-ig"
          >
            {data?.map((item, i) => (
              <SwiperSlide key={i} className="!h-[192px] !w-[192px]">
                <Link href={item.link} target="_blank">
                  <img
                    className="aspect-square h-full w-full object-cover"
                    src={maskedUrls[item.img_src] || item.img_src}
                    alt={item.alt_text}
                  />
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </div>
  );
}

const InstaIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    className="h-6 w-6 fill-[currentColor]"
  >
    <g clipPath="url(#clip0_2560_5178)">
      <path d="M12 2.26738C15.2063 2.26738 15.5859 2.28145 16.8469 2.3377C18.0188 2.38926 18.6516 2.58613 19.0734 2.7502C19.6313 2.96582 20.0344 3.22832 20.4516 3.64551C20.8734 4.06738 21.1313 4.46582 21.3469 5.02363C21.5109 5.44551 21.7078 6.08301 21.7594 7.2502C21.8156 8.51582 21.8297 8.89551 21.8297 12.0971C21.8297 15.3033 21.8156 15.683 21.7594 16.9439C21.7078 18.1158 21.5109 18.7486 21.3469 19.1705C21.1313 19.7283 20.8687 20.1314 20.4516 20.5486C20.0297 20.9705 19.6313 21.2283 19.0734 21.4439C18.6516 21.608 18.0141 21.8049 16.8469 21.8564C15.5813 21.9127 15.2016 21.9268 12 21.9268C8.79375 21.9268 8.41406 21.9127 7.15313 21.8564C5.98125 21.8049 5.34844 21.608 4.92656 21.4439C4.36875 21.2283 3.96563 20.9658 3.54844 20.5486C3.12656 20.1268 2.86875 19.7283 2.65313 19.1705C2.48906 18.7486 2.29219 18.1111 2.24063 16.9439C2.18438 15.6783 2.17031 15.2986 2.17031 12.0971C2.17031 8.89082 2.18438 8.51113 2.24063 7.2502C2.29219 6.07832 2.48906 5.44551 2.65313 5.02363C2.86875 4.46582 3.13125 4.0627 3.54844 3.64551C3.97031 3.22363 4.36875 2.96582 4.92656 2.7502C5.34844 2.58613 5.98594 2.38926 7.15313 2.3377C8.41406 2.28145 8.79375 2.26738 12 2.26738ZM12 0.106445C8.74219 0.106445 8.33438 0.120508 7.05469 0.176758C5.77969 0.233008 4.90313 0.439258 4.14375 0.73457C3.35156 1.04395 2.68125 1.45176 2.01563 2.12207C1.34531 2.7877 0.9375 3.45801 0.628125 4.24551C0.332812 5.00957 0.126563 5.88144 0.0703125 7.15645C0.0140625 8.44082 0 8.84863 0 12.1064C0 15.3643 0.0140625 15.7721 0.0703125 17.0518C0.126563 18.3268 0.332812 19.2033 0.628125 19.9627C0.9375 20.7549 1.34531 21.4252 2.01563 22.0908C2.68125 22.7564 3.35156 23.1689 4.13906 23.4736C4.90313 23.7689 5.775 23.9752 7.05 24.0314C8.32969 24.0877 8.7375 24.1018 11.9953 24.1018C15.2531 24.1018 15.6609 24.0877 16.9406 24.0314C18.2156 23.9752 19.0922 23.7689 19.8516 23.4736C20.6391 23.1689 21.3094 22.7564 21.975 22.0908C22.6406 21.4252 23.0531 20.7549 23.3578 19.9674C23.6531 19.2033 23.8594 18.3314 23.9156 17.0564C23.9719 15.7768 23.9859 15.3689 23.9859 12.1111C23.9859 8.85332 23.9719 8.44551 23.9156 7.16582C23.8594 5.89082 23.6531 5.01426 23.3578 4.25488C23.0625 3.45801 22.6547 2.7877 21.9844 2.12207C21.3188 1.45645 20.6484 1.04395 19.8609 0.739258C19.0969 0.443945 18.225 0.237695 16.95 0.181445C15.6656 0.120508 15.2578 0.106445 12 0.106445Z" />
      <path d="M12 5.94238C8.59688 5.94238 5.83594 8.70332 5.83594 12.1064C5.83594 15.5096 8.59688 18.2705 12 18.2705C15.4031 18.2705 18.1641 15.5096 18.1641 12.1064C18.1641 8.70332 15.4031 5.94238 12 5.94238ZM12 16.1049C9.79219 16.1049 8.00156 14.3143 8.00156 12.1064C8.00156 9.89863 9.79219 8.10801 12 8.10801C14.2078 8.10801 15.9984 9.89863 15.9984 12.1064C15.9984 14.3143 14.2078 16.1049 12 16.1049Z" />
      <path d="M19.8469 5.69859C19.8469 6.49546 19.2 7.13765 18.4078 7.13765C17.6109 7.13765 16.9688 6.49077 16.9688 5.69859C16.9688 4.90171 17.6156 4.25952 18.4078 4.25952C19.2 4.25952 19.8469 4.9064 19.8469 5.69859Z" />
    </g>
    <defs>
      <clipPath id="clip0_2560_5178">
        <rect
          width="24"
          height="24"
          fill="white"
          transform="translate(0 0.106445)"
        />
      </clipPath>
    </defs>
  </svg>
);
